'use client';

import { useFormContext } from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@eliadmin/core/src/components/ui/popover';
import { X } from 'lucide-react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { SparepartsFormValues } from './spareparts-wrapper';
import { ModelSearchBox, normalizeSpaces } from './model-search-box';
import { useState, useMemo, useEffect } from 'react';
import type { Model } from '../../api/spareparts-api';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';

interface ModelNode extends Model {
  children?: ModelNode[];
  isLoading?: boolean;
}

interface ModelFieldProps {
  isDisabled?: boolean;
  onClear?: () => void;
  onModelFieldFocus?: () => void;
  selectedModel?: Model | null;
  setSelectedModel?: (model: Model | null) => void;
}

export function ModelField({
  isDisabled = false,
  onClear,
  onModelFieldFocus,
  selectedModel,
  setSelectedModel,
}: ModelFieldProps) {
  const { t } = useTranslations();
  const form = useFormContext<SparepartsFormValues>();
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // State for ModelSearchBox to preserve when popover closes
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [persistentExpandedNodes, setPersistentExpandedNodes] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [modelTree, setModelTree] = useState<ModelNode[]>([]);
  const [shouldSearch, setShouldSearch] = useState<boolean>(false);

  // Get the selected brand ID from the form
  const selectedBrand = form.watch('selectedBrand');
  const brandId = selectedBrand?.id || form.watch('brandId');

  const handleClear = () => {
    form.setValue('model', null);
    form.setValue('modelName', '');
    setSelectedModel?.(null);
    onClear?.();

    // Clear modelArticleNumber from URL
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('modelArticleNumber');
    router.replace(`${pathname}?${newSearchParams.toString()}`, { scroll: false });
  };

  const handleModelSelect = (model: Model) => {
    setSelectedModel?.(model);
    // Store both the full model object and the display name
    form.setValue('model', model);
    const modelName = (model.description || model.articleNumber || model.id || '')
      .replace(/\s+/g, ' ')
      .trim();
    form.setValue('modelName', modelName);
    setIsOpen(false);

    // Update URL with model article number
    if (model?.articleNumber) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('modelArticleNumber', model.articleNumber);
      // Use replace with scroll: false to prevent page scroll
      router.replace(`${pathname}?${newSearchParams.toString()}`, { scroll: false });
    }
  };

  // Get display name from the full model object
  const modelDisplayName = useMemo(
    () =>
      selectedModel
        ? selectedModel.description || selectedModel.articleNumber || selectedModel.id || ''
        : '',
    [selectedModel],
  );

  // Update search query when selected model changes
  const initialSearchQuery = selectedModel?.description || selectedModel?.articleNumber || '';

  return (
    <FormField
      control={form.control}
      name="model"
      render={({ field }) => (
        <FormItem className="col-span-2">
          <FormLabel className="text-sm font-medium">{t('Model')}</FormLabel>
          <FormControl>
            <Popover
              open={!isDisabled && isOpen}
              onOpenChange={open => !isDisabled && setIsOpen(open)}
            >
              <PopoverTrigger asChild>
                <div
                  className="relative"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isDisabled && !isOpen) {
                      setIsOpen(true);
                    }
                  }}
                >
                  <Input
                    placeholder={t('Enter model')}
                    disabled={isDisabled}
                    readOnly
                    className={{
                      wrapper: cn(isDisabled && 'cursor-not-allowed bg-gray-100 opacity-50'),
                      main: cn(isDisabled && 'cursor-not-allowed bg-gray-100 opacity-50'),
                    }}
                    value={modelDisplayName ? normalizeSpaces(modelDisplayName) : ''}
                    name={field.name}
                    ref={field.ref}
                    autoComplete="off"
                    onFocus={onModelFieldFocus}
                  />
                  {selectedModel && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                      onClick={handleClear}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </PopoverTrigger>
              <PopoverContent
                className="mt-0 p-0"
                align="start"
                sideOffset={1}
                side="bottom"
                onOpenAutoFocus={e => e.preventDefault()}
                style={{ width: 'var(--radix-popover-trigger-width)' }}
              >
                <ModelSearchBox
                  onSelect={handleModelSelect}
                  selectedModel={selectedModel}
                  initialSearchQuery={initialSearchQuery}
                  brandId={brandId}
                  className="w-full"
                  // State props to preserve state when popover closes
                  expandedNodes={expandedNodes}
                  setExpandedNodes={setExpandedNodes}
                  persistentExpandedNodes={persistentExpandedNodes}
                  setPersistentExpandedNodes={setPersistentExpandedNodes}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  modelTree={modelTree}
                  setModelTree={setModelTree}
                  shouldSearch={shouldSearch}
                  setShouldSearch={setShouldSearch}
                />
              </PopoverContent>
            </Popover>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
